// pages/ContactDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Building2,
  User,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  DollarSign,
  Users,
  ExternalLink,
  Plus,
  Briefcase,
  AlertCircle,
  Calendar,
  Save,
  X as CloseIcon,
  Loader,
  Home,
  Smartphone,
  PhoneCall,
  UserCheck,
  Cake,
  Languages,
  CheckSquare,
} from "lucide-react";
import apiService from "../../services/api";
import EditContactModal from "../../components/EditContactModal";
import CreateOpportunityModal from "../../components/CreateOpportunityModal";
import CreateCaseModal from "../../components/CreateCaseModal";
import PhoneLink from "../../components/PhoneLink";
import { useTheme } from "../../contexts/ThemeContext";

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [activeRelatedTab, setActiveRelatedTab] = useState("opportunities");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Modal states
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);
  const [showCreateCase, setShowCreateCase] = useState(false);

  // Related data
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [cases, setCases] = useState([]);

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  // Drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);

  useEffect(() => {
    fetchContact();
    fetchNotes();
    fetchAttachments();
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContact(id);
      setContact(response.data);
      if (response.data.opportunities)
        setOpportunities(response.data.opportunities);
      if (response.data.cases) setCases(response.data.cases);
    } catch (error) {
      console.error("Error fetching contact:", error);
      navigate("/contacts");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getContactNotes(id);
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getContactAttachments(id);
      setAttachments(response.data || []);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await apiService.deleteContact(id);
        navigate("/contacts");
      } catch (error) {
        console.error("Error deleting contact:", error);
        if (error.response?.data?.relatedRecords) {
          alert(
            `Cannot delete contact. It has:\n` +
              `${error.response.data.relatedRecords.opportunities} opportunities\n` +
              `${error.response.data.relatedRecords.cases} cases`,
          );
        } else {
          alert("Failed to delete contact");
        }
      }
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await apiService.createContactNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadContactAttachment(id, formData);
      fetchAttachments();
      event.target.value = "";
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?"))
      return;

    try {
      await apiService.deleteContactAttachment(id, attachmentId);
      fetchAttachments();
    } catch (error) {
      console.error("Error deleting attachment:", error);
      alert("Failed to delete attachment");
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFilesToUpload(files);
      setShowUploadConfirmation(true);
    }
  };

  const confirmUpload = async () => {
    if (!filesToUpload) return;

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        await apiService.uploadContactAttachment(id, formData);
      }
      fetchAttachments();
      setShowUploadConfirmation(false);
      setFilesToUpload(null);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files");
    }
  };

  const cancelUpload = () => {
    setShowUploadConfirmation(false);
    setFilesToUpload(null);
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      await apiService.updateContact(id, { [field]: editingValue });
      await fetchContact();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith("image/")) return "🖼️";
    if (fileType?.startsWith("video/")) return "🎥";
    if (fileType?.startsWith("audio/")) return "🎵";
    if (fileType?.includes("pdf")) return "📄";
    if (fileType?.includes("word") || fileType?.includes("document"))
      return "📝";
    if (fileType?.includes("sheet") || fileType?.includes("excel")) return "📊";
    if (fileType?.includes("presentation") || fileType?.includes("powerpoint"))
      return "📽️";
    if (fileType?.includes("zip") || fileType?.includes("rar")) return "🗜️";
    return "📎";
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  rows={3}
                  autoFocus
                />
              ) : type === "date" ? (
                <input
                  type="date"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  autoFocus
                />
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  autoFocus
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {type === "date" && value
                  ? new Date(value).toLocaleDateString()
                  : value || "—"}
              </p>
              <button
                onClick={() => startEditing(field, value)}
                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!contact) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Contact not found
        </div>
      </div>
    );
  }

  const fullName =
    `${contact.salutation || ""} ${contact.firstName || ""} ${contact.lastName || ""}`.trim();

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => setShowCreateOpportunity(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <DollarSign size={16} />
                <span className="hidden sm:inline">New Opportunity</span>
              </button>

              <button
                onClick={() => setShowCreateCase(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <AlertCircle size={16} />
                <span className="hidden sm:inline">New Case</span>
              </button>

              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t my-1 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  {getInitials(contact.firstName, contact.lastName)}
                </div>
                <div>
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {fullName}
                  </h1>
                  {contact.title && (
                    <div
                      className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Briefcase size={14} />
                      {contact.title}
                      {contact.department && ` • ${contact.department}`}
                    </div>
                  )}
                </div>

                {contact.level && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {contact.level}
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {contact.account && (
                  <button
                    onClick={() => navigate(`/accounts/${contact.account.id}`)}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Building2 size={16} />
                    <span>{contact.account.name}</span>
                  </button>
                )}
                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Mail size={16} />
                    <span>{contact.email}</span>
                  </a>
                )}
                {contact.phone && (
                  <PhoneLink
                    phoneNumber={contact.phone}
                    entityType="contact"
                    entityId={contact.id}
                    entityName={`${contact.firstName} ${contact.lastName}`}
                  />
                )}
                {contact.mobilePhone && (
                  <PhoneLink
                    phoneNumber={contact.mobilePhone}
                    entityType="contact"
                    entityId={contact.id}
                    entityName={`${contact.firstName} ${contact.lastName}`}
                  />
                )}
              </div>
            </div>

            {contact.owner && (
              <div className="flex flex-row sm:flex-col gap-3 flex-wrap">
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                  >
                    {contact.owner.profileImageUrl ? (
                      <img
                        src={contact.owner.profileImageUrl}
                        alt={contact.owner.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        {contact.owner.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Contact Owner
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {contact.owner.name}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b overflow-x-auto scrollbar-hide ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  {
                    id: "related",
                    label: "Related",
                    icon: Activity,
                    count: opportunities.length + cases.length,
                  },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? "text-blue-400 border-b-2 border-blue-400"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                        {tab.count > 0 && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                          >
                            {tab.count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <User size={18} />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Salutation"
                          field="salutation"
                          value={contact.salutation}
                          options={[
                            { value: "Mr.", label: "Mr." },
                            { value: "Ms.", label: "Ms." },
                            { value: "Mrs.", label: "Mrs." },
                            { value: "Dr.", label: "Dr." },
                            { value: "Prof.", label: "Prof." },
                          ]}
                        />
                        <EditableField
                          label="First Name"
                          field="firstName"
                          value={contact.firstName}
                        />
                        <EditableField
                          label="Last Name"
                          field="lastName"
                          value={contact.lastName}
                        />
                        <EditableField
                          label="Suffix"
                          field="suffix"
                          value={contact.suffix}
                        />
                        <EditableField
                          label="Title"
                          field="title"
                          value={contact.title}
                        />
                        <EditableField
                          label="Department"
                          field="department"
                          value={contact.department}
                        />
                        <EditableField
                          label="Birthdate"
                          field="birthdate"
                          value={contact.birthdate}
                          type="date"
                        />
                        <EditableField
                          label="Level"
                          field="level"
                          value={contact.level}
                          options={[
                            { value: "Primary", label: "Primary" },
                            { value: "Secondary", label: "Secondary" },
                            { value: "Tertiary", label: "Tertiary" },
                          ]}
                        />
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <PhoneCall size={18} />
                        Contact Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Email"
                          field="email"
                          value={contact.email}
                          type="email"
                        />
                        <EditableField
                          label="Phone"
                          field="phone"
                          value={contact.phone}
                          type="tel"
                        />
                        <EditableField
                          label="Mobile Phone"
                          field="mobilePhone"
                          value={contact.mobilePhone}
                          type="tel"
                        />
                        <EditableField
                          label="Home Phone"
                          field="homePhone"
                          value={contact.homePhone}
                          type="tel"
                        />
                        <EditableField
                          label="Other Phone"
                          field="otherPhone"
                          value={contact.otherPhone}
                          type="tel"
                        />
                        <EditableField
                          label="Fax"
                          field="fax"
                          value={contact.fax}
                          type="tel"
                        />
                        <EditableField
                          label="Assistant Name"
                          field="assistantName"
                          value={contact.assistantName}
                        />
                        <EditableField
                          label="Assistant Phone"
                          field="assistantPhone"
                          value={contact.assistantPhone}
                          type="tel"
                        />
                      </div>
                    </div>

                    {/* Address Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <MapPin size={18} />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4
                            className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Mailing Address
                          </h4>
                          <EditableField
                            label="Street"
                            field="mailingStreet"
                            value={contact.mailingStreet}
                            type="textarea"
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                              label="City"
                              field="mailingCity"
                              value={contact.mailingCity}
                            />
                            <EditableField
                              label="State"
                              field="mailingState"
                              value={contact.mailingState}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                              label="Postal Code"
                              field="mailingPostalCode"
                              value={contact.mailingPostalCode}
                            />
                            <EditableField
                              label="Country"
                              field="mailingCountry"
                              value={contact.mailingCountry}
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4
                            className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Other Address
                          </h4>
                          <EditableField
                            label="Street"
                            field="otherStreet"
                            value={contact.otherStreet}
                            type="textarea"
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                              label="City"
                              field="otherCity"
                              value={contact.otherCity}
                            />
                            <EditableField
                              label="State"
                              field="otherState"
                              value={contact.otherState}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                              label="Postal Code"
                              field="otherPostalCode"
                              value={contact.otherPostalCode}
                            />
                            <EditableField
                              label="Country"
                              field="otherCountry"
                              value={contact.otherCountry}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Additional Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Lead Source"
                          field="leadSource"
                          value={contact.leadSource}
                          options={[
                            { value: "Web", label: "Web" },
                            { value: "Phone Inquiry", label: "Phone Inquiry" },
                            {
                              value: "Partner Referral",
                              label: "Partner Referral",
                            },
                            {
                              value: "Purchased List",
                              label: "Purchased List",
                            },
                            { value: "Other", label: "Other" },
                          ]}
                        />
                        <EditableField
                          label="Languages"
                          field="languages"
                          value={contact.languages}
                        />
                      </div>
                      <div className="mt-4">
                        <EditableField
                          label="Description"
                          field="description"
                          value={contact.description}
                          type="textarea"
                        />
                      </div>
                    </div>

                    {/* Email Preferences */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Communication Preferences
                      </h3>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={contact.hasOptedOutOfEmail}
                            onChange={async (e) => {
                              try {
                                await apiService.updateContact(id, {
                                  hasOptedOutOfEmail: e.target.checked,
                                });
                                fetchContact();
                              } catch (error) {
                                console.error(
                                  "Error updating preference:",
                                  error,
                                );
                              }
                            }}
                            className="rounded"
                          />
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Opted out of email
                          </span>
                        </label>
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={contact.hasOptedOutOfFax}
                            onChange={async (e) => {
                              try {
                                await apiService.updateContact(id, {
                                  hasOptedOutOfFax: e.target.checked,
                                });
                                fetchContact();
                              } catch (error) {
                                console.error(
                                  "Error updating preference:",
                                  error,
                                );
                              }
                            }}
                            className="rounded"
                          />
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Opted out of fax
                          </span>
                        </label>
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={contact.doNotCall}
                            onChange={async (e) => {
                              try {
                                await apiService.updateContact(id, {
                                  doNotCall: e.target.checked,
                                });
                                fetchContact();
                              } catch (error) {
                                console.error(
                                  "Error updating preference:",
                                  error,
                                );
                              }
                            }}
                            className="rounded"
                          />
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Do not call
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <User size={16} />
                            {contact.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(contact.createdAt).toLocaleString()}
                          </p>
                        </div>
                        {contact.lastModifiedBy && (
                          <>
                            <div>
                              <label
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Last Modified By
                              </label>
                              <p
                                className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                <User size={16} />
                                {contact.lastModifiedBy.name}
                              </p>
                            </div>
                            <div>
                              <label
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Last Modified Date
                              </label>
                              <p
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {new Date(contact.updatedAt).toLocaleString()}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Reporting Structure */}
                    {(contact.reportsTo ||
                      contact.directReports?.length > 0) && (
                      <div
                        className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <h3
                          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Reporting Structure
                        </h3>
                        {contact.reportsTo && (
                          <div className="mb-4">
                            <label
                              className={`text-sm block mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Reports To
                            </label>
                            <button
                              onClick={() =>
                                navigate(`/contacts/${contact.reportsTo.id}`)
                              }
                              className={`flex items-center gap-3 p-3 rounded-lg transition-colors w-full ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                            >
                              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
                                {getInitials(
                                  contact.reportsTo.firstName,
                                  contact.reportsTo.lastName,
                                )}
                              </div>
                              <div className="flex-1 text-left">
                                <div
                                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {contact.reportsTo.firstName}{" "}
                                  {contact.reportsTo.lastName}
                                </div>
                                <div
                                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {contact.reportsTo.title}
                                </div>
                              </div>
                              <ExternalLink
                                size={16}
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-400"
                                }
                              />
                            </button>
                          </div>
                        )}

                        {contact.directReports?.length > 0 && (
                          <div>
                            <label
                              className={`text-sm block mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Direct Reports ({contact.directReports.length})
                            </label>
                            <div className="space-y-2">
                              {contact.directReports.map((report) => (
                                <button
                                  key={report.id}
                                  onClick={() =>
                                    navigate(`/contacts/${report.id}`)
                                  }
                                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                                >
                                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
                                    {getInitials(
                                      report.firstName,
                                      report.lastName,
                                    )}
                                  </div>
                                  <div className="flex-1 text-left">
                                    <div
                                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                    >
                                      {report.firstName} {report.lastName}
                                    </div>
                                    <div
                                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                    >
                                      {report.title}
                                    </div>
                                  </div>
                                  <ExternalLink
                                    size={16}
                                    className={
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-400"
                                    }
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Related Tab */}
                {activeTab === "related" && (
                  <div className="space-y-6">
                    {/* Sub-tabs for Related */}
                    <div
                      className={`flex flex-wrap gap-2 border-b pb-2 mb-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      {[
                        {
                          id: "opportunities",
                          label: `Opportunities (${opportunities.length})`,
                          icon: DollarSign,
                        },
                        {
                          id: "cases",
                          label: `Cases (${cases.length})`,
                          icon: AlertCircle,
                        },
                      ].map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveRelatedTab(tab.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              activeRelatedTab === tab.id
                                ? "bg-blue-600 text-white"
                                : isDarkMode
                                  ? "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700"
                                  : "bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <Icon size={14} />
                              {tab.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Opportunities */}
                    {activeRelatedTab === "opportunities" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <DollarSign size={18} />
                            Opportunities ({opportunities.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateOpportunity(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {opportunities.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <DollarSign
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No opportunities yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {opportunities.map((opp) => (
                              <div
                                key={opp.id}
                                onClick={() =>
                                  navigate(`/opportunities/${opp.id}`)
                                }
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {opp.name}
                                  </div>
                                  <div
                                    className={`flex items-center gap-3 mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    <span className="text-green-400">
                                      £{opp.amount?.toLocaleString() || "0"}
                                    </span>
                                    <span>•</span>
                                    <span className="capitalize">
                                      {opp.salesStatus?.replace("_", " ")}
                                    </span>
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cases */}
                    {activeRelatedTab === "cases" && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            <AlertCircle size={18} />
                            Cases ({cases.length})
                          </h3>
                          <button
                            onClick={() => setShowCreateCase(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                          >
                            <Plus size={16} />
                            New
                          </button>
                        </div>
                        {cases.length === 0 ? (
                          <div
                            className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            <AlertCircle
                              size={48}
                              className="mx-auto mb-2 opacity-50"
                            />
                            <p>No cases yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {cases.map((caseItem) => (
                              <div
                                key={caseItem.id}
                                onClick={() =>
                                  navigate(`/cases/${caseItem.id}`)
                                }
                                className={`p-3 rounded-lg transition-colors cursor-pointer flex items-center justify-between ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {caseItem.subject}
                                  </div>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {caseItem.caseNumber} • {caseItem.status}
                                  </div>
                                </div>
                                <ExternalLink
                                  size={14}
                                  className={
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-400"
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Notes */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex items-center justify-between mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <h3 className="font-semibold">Notes ({notes.length})</h3>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg mb-2 focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Attachments ({attachments.length})
                </h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm">
                    <Plus size={16} />
                    Upload
                  </div>
                </label>
              </div>

              {attachments.length === 0 ? (
                <div
                  className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <Paperclip size={48} className="mx-auto mb-2 opacity-50" />
                  <p className="mb-4">No attachments</p>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                      <Plus size={16} />
                      Upload First File
                    </div>
                  </label>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className={`group flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                    >
                      <div className="text-2xl">
                        {getFileIcon(attachment.contentType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          title={attachment.name}
                        >
                          {attachment.name}
                        </div>
                        <div
                          className={`text-xs flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <span>{formatFileSize(attachment.bodyLength)}</span>
                          <span>•</span>
                          <span>
                            {new Date(
                              attachment.createdAt,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {attachment.fileUrl && (
                          <a
                            href={attachment.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                            title="Download"
                          >
                            <ExternalLink size={16} className="text-blue-400" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditContactModal
          contact={contact}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchContact();
          }}
        />
      )}

      {showCreateOpportunity && (
        <CreateOpportunityModal
          contact={contact}
          account={contact.account}
          onClose={() => setShowCreateOpportunity(false)}
          onSuccess={() => {
            setShowCreateOpportunity(false);
            fetchContact();
          }}
        />
      )}

      {showCreateCase && (
        <CreateCaseModal
          contact={contact}
          account={contact.account}
          onClose={() => setShowCreateCase(false)}
          onSuccess={() => {
            setShowCreateCase(false);
            fetchContact();
          }}
        />
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
          <div
            className={`rounded-lg border-2 border-dashed border-blue-500 p-8 text-center ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
          >
            <Paperclip size={48} className="mx-auto mb-4 text-blue-400" />
            <p
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Drop files to upload
            </p>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Release to add files to this contact
            </p>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal */}
      {showUploadConfirmation && filesToUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Paperclip className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Upload Files?
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {filesToUpload.length === 1
                    ? `Upload "${filesToUpload[0].name}" to this contact?`
                    : `Upload ${filesToUpload.length} files to this contact?`}
                </p>

                {filesToUpload.length > 1 && (
                  <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                    {Array.from(filesToUpload).map((file, idx) => (
                      <div
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Paperclip size={14} />
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelUpload}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetail;
